import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Users,
  Truck,
  ShoppingCart,
  Package,
  Wrench,
  Receipt,
  CreditCard,
  ArrowLeftRight,
  Wallet,
  Building2,
  CreditCardIcon,
  Calendar,
  FileBarChart,
  Database,
  UserCog,
  LogOut,
  Menu,
  X,
  FileDown,
  FileUp,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const menuItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, section: null },
  { path: "/agenda", label: "Ajanda", icon: Calendar },
  { section: "İşlemler" },
  { path: "/sales", label: "Satış", icon: ShoppingCart },
  { path: "/purchases", label: "Mal Alış", icon: Package },
  { path: "/service-purchases", label: "Hizmet Alış", icon: Wrench },
  { path: "/credit-notes", label: "Alacak Dekontu", icon: FileDown },
  { path: "/debit-notes", label: "Borç Dekontu", icon: FileUp },
  { section: "Cariler" },
  { path: "/customers", label: "Müşteriler", icon: Users },
  { path: "/suppliers", label: "Tedarikçiler", icon: Truck },
  { section: "Finans" },
  { path: "/collections", label: "Tahsilat", icon: Receipt },
  { path: "/payments", label: "Ödeme", icon: CreditCard },
  { path: "/offsets", label: "Mahsup", icon: ArrowLeftRight },
  { path: "/expenses", label: "Gider", icon: Wallet },
  { section: "Hesaplar" },
  { path: "/cash-accounts", label: "Kasa Hesabı", icon: Wallet },
  { path: "/bank-accounts", label: "Banka Hesabı", icon: Building2 },
  { path: "/credit-cards", label: "Kredi Kartı", icon: CreditCardIcon },
  { section: "Diğer" },
  { path: "/reports", label: "Raporlar", icon: FileBarChart },
  { path: "/backup", label: "Veri Yönetimi", icon: Database },
];

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const renderMenuItem = (item, mobile = false) => {
    // Section header
    if (item.section) {
      if (!sidebarOpen && !mobile) return null;
      return (
        <div key={item.section} className="pt-4 pb-1 px-3 first:pt-0">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {item.section}
          </span>
        </div>
      );
    }

    // Regular menu item
    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.path === "/"}
        onClick={() => mobile && setMobileMenuOpen(false)}
        data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
            isActive
              ? "bg-slate-100 text-slate-900 font-medium"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          } ${!sidebarOpen && !mobile ? "justify-center" : ""}`
        }
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        {(sidebarOpen || mobile) && <span>{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-md hover:bg-slate-100"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <h1 className="font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Ön Muhasebe
        </h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 w-9 rounded-full bg-slate-100">
              {user?.full_name?.charAt(0) || "U"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Çıkış Yap
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile sidebar */}
      <div
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 transform transition-transform duration-200 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-4 border-b border-slate-200">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ön Muhasebe
          </h1>
        </div>
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-140px)]">
          {menuItems.map(item => renderMenuItem(item, true))}
          {isAdmin && (
            <NavLink
              to="/users"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-slate-100 text-slate-900 font-medium"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <UserCog className="h-5 w-5 flex-shrink-0" />
              <span>Kullanıcılar</span>
            </NavLink>
          )}
        </nav>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          className={`hidden lg:flex flex-col bg-white border-r border-slate-200 sticky top-0 h-screen transition-sidebar ${
            sidebarOpen ? "w-64" : "w-20"
          }`}
        >
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Ön Muhasebe
              </h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-slate-100"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map(item => renderMenuItem(item))}
            {isAdmin && (
              <NavLink
                to="/users"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-slate-100 text-slate-900 font-medium"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  } ${!sidebarOpen ? "justify-center" : ""}`
                }
              >
                <UserCog className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span>Kullanıcılar</span>}
              </NavLink>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className={`flex items-center gap-3 ${!sidebarOpen ? "justify-center" : ""}`}>
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-medium text-slate-700">
                {user?.full_name?.charAt(0) || "U"}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.role === "admin" ? "Yönetici" : "Muhasebeci"}</p>
                </div>
              )}
              {sidebarOpen && (
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-screen">
          <div className="p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
